const YT_HOST = 'https://www.youtube-nocookie.com/embed/';
const VIMEO_HOST = 'https://player.vimeo.com/video/';
const TIKTOK_EMBED = 'https://www.tiktok.com/embed/v2/';
const IG_EMBED_SUFFIX = '/embed/captioned';

const DIRECT_VIDEO_RE = /\.(mp4|webm|ogv|ogg|m4v|mov)(?:[?#]|$)/i;
const YT_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i;
const VIMEO_RE = /vimeo\.com\/(?:video\/)?(\d{6,})/i;
const TIKTOK_RE = /tiktok\.com\/(?:@[^/]+\/video\/|v\/|embed\/v2\/)(\d{6,})/i;
const IG_RE = /instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/i;

function detectMediaType(url) {
  if (!url) return { kind: 'none' };

  // Recognised iframe providers first (they DON'T look like videos to <video>).
  const yt = url.match(YT_RE);
  if (yt) {
    const id = yt[1];
    return {
      kind: 'iframe',
      provider: 'youtube',
      embedUrl: `${YT_HOST}${id}?autoplay=1&mute=1&loop=1&playlist=${id}&playsinline=1&controls=1&modestbranding=1&rel=0`,
    };
  }

  const vimeo = url.match(VIMEO_RE);
  if (vimeo) {
    const id = vimeo[1];
    return {
      kind: 'iframe',
      provider: 'vimeo',
      embedUrl: `${VIMEO_HOST}${id}?autoplay=1&muted=1&loop=1&playsinline=1`,
    };
  }

  const tt = url.match(TIKTOK_RE);
  if (tt) {
    return {
      kind: 'iframe',
      provider: 'tiktok',
      embedUrl: `${TIKTOK_EMBED}${tt[1]}`,
    };
  }

  const ig = url.match(IG_RE);
  if (ig) {
    return {
      kind: 'iframe',
      provider: 'instagram',
      embedUrl: `https://www.instagram.com/p/${ig[1]}${IG_EMBED_SUFFIX}/`,
    };
  }

  // Everything else (direct .mp4, snaptik proxies, signed CDN URLs without
  // a .mp4 extension, etc.) — treat as direct <video> so our mute / progress
  // controls can drive it. If the URL isn't actually a video, the <video>
  // element will simply emit `error` and the slide stays on its poster.
  return { kind: 'direct', src: url };
}

function isControllable(mediaInfo) {
  return mediaInfo.kind === 'direct';
}

class AsSeenOnYou extends HTMLElement {
  constructor() {
    super();
    this._triggers = [];
    this._slides = [];
    this._mediaInfo = [];
    this._scrollRestore = 0;
    this._intersectionObserver = null;
    this._currentIndex = 0;
    this._unmuted = false;
    this._hydrated = false;
    this._activeVideo = null;
    this._activeTimeUpdate = null;
    this._desktopMq = window.matchMedia?.('(min-width: 750px)');
    this._openMode = null; // 'pip' | 'fullscreen'
  }

  connectedCallback() {
    queueMicrotask(() => this._hydrate());
  }

  disconnectedCallback() {
    this._teardownObserver();
    this._detachActiveVideoListeners();
    this._dialog?.removeEventListener('close', this._handleDialogClose);
    this._dialog?.removeEventListener('click', this._handleDialogClick);
    this.removeEventListener('click', this._handleTriggerClick);
    document.removeEventListener('keydown', this._handleKeyDown);
    this._desktopMq?.removeEventListener?.('change', this._handleBreakpointChange);

    // Remove the portaled dialog from <body> so we don't leak between
    // theme-editor re-renders.
    if (this._dialog && this._dialog.parentElement === document.body) {
      this._dialog.remove();
    }
  }

  _hydrate() {
    if (this._hydrated) return;

    this._dialog = this.querySelector('[data-asoy-modal]');
    this._track = this.querySelector('[data-asoy-track]');
    if (!this._dialog || !this._track) return;

    // Portal the dialog to <body> so it escapes any ancestor stacking-context
    // cap (transforms, fixed/sticky parents, third-party app embeds). Without
    // this, even z-index: 2147483647 can be beaten by a sibling section.
    if (this._dialog.parentElement !== document.body) {
      this._dialogHome = this._dialog.parentElement;
      document.body.appendChild(this._dialog);
    }

    const templates = this.querySelectorAll('template[data-asoy-slide]');
    if (templates.length === 0) return;

    for (const tpl of templates) {
      const fragment = tpl.content.cloneNode(true);
      const slide = fragment.querySelector('.asoy-slide');
      if (!slide) continue;

      const externalUrl = slide.dataset.asoyExternalUrl || '';
      const mediaType = slide.dataset.asoyMediaType;

      // External-URL classification has to come BEFORE the "has a <video>
      // element" check, because we now eagerly render a <video> for external
      // URLs too (so iOS gets the cached first frame). data-asoy-external-url
      // is the canonical signal for an external item.
      let info = { kind: 'none' };
      if (mediaType === 'image') {
        info = { kind: 'image' };
      } else if (externalUrl) {
        info = detectMediaType(externalUrl);
      } else if (slide.querySelector('video[data-asoy-video]')) {
        info = { kind: 'uploaded' };
      }

      slide.dataset.asoyControllable = isControllable(info) || info.kind === 'uploaded' ? 'true' : 'false';
      this._track.appendChild(fragment);
      this._slides.push(slide);
      this._mediaInfo.push(info);
    }

    this._triggers = Array.from(this.querySelectorAll('[data-asoy-trigger]'));

    // Triggers live inside `this`; dialog buttons live on the (portaled) dialog.
    // Two listeners means the dialog can move to <body> without breaking click handling.
    this.addEventListener('click', this._handleTriggerClick);
    this._dialog.addEventListener('click', this._handleDialogClick);
    this._dialog.addEventListener('close', this._handleDialogClose);

    this._hydrated = true;

    this._setupPosterExtraction();
  }

  // For each external-URL item without a manual cover image, lazily extract
  // the first frame and apply it to the carousel thumbnail (and the modal
  // slide's <video poster>). Done on intersection so we never extract for
  // items the user never sees.
  _setupPosterExtraction() {
    const targets = this._triggers.filter((t) => t.dataset.asoyExtractFrom);
    if (targets.length === 0) return;

    const apply = async (trigger) => {
      const url = trigger.dataset.asoyExtractFrom;
      if (!url || trigger.dataset.asoyPosterApplied) return;
      trigger.dataset.asoyPosterApplied = 'pending';

      const dataUrl = await extractPoster(url);
      if (!dataUrl) {
        trigger.dataset.asoyPosterApplied = 'failed';
        return;
      }

      // 1. Carousel thumbnail — paint the dataURL behind the placeholder SVG.
      const slot = trigger.querySelector('[data-asoy-poster-slot]');
      if (slot) {
        slot.style.backgroundImage = `url("${dataUrl}")`;
        slot.style.backgroundSize = 'cover';
        slot.style.backgroundPosition = 'center';
        const svg = slot.querySelector('svg');
        if (svg) svg.style.display = 'none';
      }

      // 2. Modal slide poster — swap the placeholder SVG for a real <img>
      //    so it's visible immediately on scroll, before the video loads.
      const triggerIndex = this._triggers.indexOf(trigger);
      const slide = this._slides[triggerIndex];
      const slidePoster = slide?.querySelector('[data-asoy-slide-poster]');
      if (slidePoster && !slidePoster.querySelector('img[data-asoy-auto]')) {
        slidePoster.replaceChildren();
        const img = document.createElement('img');
        img.src = dataUrl;
        img.alt = '';
        img.className = 'asoy-slide__poster-img';
        img.dataset.asoyAuto = '';
        slidePoster.appendChild(img);
      }
      // 3. Pre-seed the video element's poster attribute if it already exists.
      const slideVideo = slide?.querySelector('video[data-asoy-video]');
      if (slideVideo && !slideVideo.poster) slideVideo.poster = dataUrl;

      trigger.dataset.asoyPosterApplied = 'true';
    };

    if (typeof IntersectionObserver !== 'function') {
      for (const t of targets) apply(t);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            apply(entry.target);
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '200px 0px' }
    );
    for (const t of targets) io.observe(t);
  }

  _handleTriggerClick = (event) => {
    const trigger = event.target.closest('[data-asoy-trigger]');
    if (!trigger || !this.contains(trigger)) return;
    event.preventDefault();
    const index = this._triggers.indexOf(trigger);
    if (index >= 0) this.open(index);
  };

  _handleDialogClick = (event) => {
    if (event.target.closest('[data-asoy-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.target.closest('[data-asoy-prev]')) {
      event.preventDefault();
      this.goto(this._currentIndex - 1);
      return;
    }
    if (event.target.closest('[data-asoy-next]')) {
      event.preventDefault();
      this.goto(this._currentIndex + 1);
      return;
    }
    if (event.target.closest('[data-asoy-mute]')) {
      event.preventDefault();
      this._toggleMute();
      return;
    }
    if (event.target.closest('[data-asoy-playpause]')) {
      event.preventDefault();
      this._togglePlayPause();
      return;
    }
    // Backdrop click closes only in fullscreen mode; PIP is persistent.
    if (this._openMode === 'fullscreen' && this._dialog?.open && event.target === this._dialog) {
      this.close();
    }
  };

  open(index = 0) {
    if (!this._dialog || this._dialog.open) return;
    if (this._slides.length === 0) return;

    const isDesktop = !!this._desktopMq?.matches;
    this._openMode = isDesktop ? 'pip' : 'fullscreen';

    if (isDesktop) {
      // Non-modal: page stays interactive, no backdrop, no scroll lock.
      this._dialog.show();
    } else {
      this._scrollRestore = window.scrollY;
      document.body.classList.add('asoy-modal-open');
      this._dialog.showModal();
    }

    // Listen on document so PIP mode (non-modal, no focus trap) still
    // catches ESC / arrow keys even when focus is elsewhere on the page.
    document.addEventListener('keydown', this._handleKeyDown);
    this._desktopMq?.addEventListener?.('change', this._handleBreakpointChange);

    // ⭐ iOS Safari only allows muted-inline autoplay when video.play() is
    // called SYNCHRONOUSLY inside the user-gesture handler. Activating the
    // initial slide here (still inside the click) preserves that context;
    // doing it inside requestAnimationFrame / IntersectionObserver would
    // lose it and the first play() call would get silently rejected.
    const clamped = Math.max(0, Math.min(this._slides.length - 1, index));
    const initialSlide = this._slides[clamped];
    if (initialSlide) {
      this._currentIndex = clamped;
      this._activateSlide(initialSlide);
    }

    // The user clicked into the gallery — that's a strong intent signal to
    // scroll between slides. Promote neighbouring videos from
    // preload="metadata" to "auto" so the next swipe doesn't have to wait
    // for a network round-trip. (On iOS Safari "auto" still gets clamped
    // to "metadata", but the data is already cached from the carousel
    // thumbnail render so this is a no-op there anyway.)
    this._warmNeighbours(clamped);

    this._setupObserver();
    requestAnimationFrame(() => this.goto(clamped, true));
  }

  _warmNeighbours(centerIndex, radius = 2) {
    for (let offset = 1; offset <= radius; offset++) {
      for (const dir of [1, -1]) {
        const i = centerIndex + offset * dir;
        if (i < 0 || i >= this._slides.length) continue;
        const v = this._slides[i]?.querySelector('video[data-asoy-video]');
        if (v) v.preload = 'auto';
      }
    }
  }

  close() {
    if (!this._dialog || !this._dialog.open) return;
    this._dialog.close();
  }

  goto(index, instant = false) {
    if (this._slides.length === 0) return;
    const clamped = Math.max(0, Math.min(this._slides.length - 1, index));
    const slide = this._slides[clamped];
    if (!slide || !this._track) return;

    this._track.scrollTo({
      top: slide.offsetTop,
      behavior: instant || prefersReducedMotion() ? 'instant' : 'smooth',
    });
    this._currentIndex = clamped;
    this._updateNavButtons();
  }

  _updateNavButtons() {
    const prev = this._dialog?.querySelector('[data-asoy-prev]');
    const next = this._dialog?.querySelector('[data-asoy-next]');
    if (prev) prev.toggleAttribute('disabled', this._currentIndex <= 0);
    if (next) next.toggleAttribute('disabled', this._currentIndex >= this._slides.length - 1);
  }

  _handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    // In PIP mode the page stays interactive — only steal navigation keys
    // when the user is actually interacting with the dialog.
    if (this._openMode === 'pip' && !this._dialog?.contains(document.activeElement)) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.goto(this._currentIndex + 1);
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goto(this._currentIndex - 1);
      return;
    }
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this._togglePlayPause();
    }
  };

  _handleDialogClose = () => {
    document.removeEventListener('keydown', this._handleKeyDown);
    this._desktopMq?.removeEventListener?.('change', this._handleBreakpointChange);
    this._teardownObserver();
    this._detachActiveVideoListeners();
    this._pauseAllMedia();

    if (this._openMode === 'fullscreen') {
      document.body.classList.remove('asoy-modal-open');
      window.scrollTo({ top: this._scrollRestore, behavior: 'instant' });
    }
    this._openMode = null;
  };

  // Close on breakpoint change — opening cleanly in the new mode requires
  // the user to re-open (mirrors what YouTube/Reels do for the same reason).
  _handleBreakpointChange = () => {
    if (this._dialog?.open) this.close();
  };

  _setupObserver() {
    if (this._intersectionObserver) return;

    this._intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const slide = entry.target;
          const ratio = entry.intersectionRatio;
          if (ratio >= 0.6) {
            this._activateSlide(slide);
            const idx = this._slides.indexOf(slide);
            if (idx >= 0) {
              this._currentIndex = idx;
              this._updateNavButtons();
            }
          } else if (ratio < 0.2) {
            this._deactivateSlide(slide);
          }
        }
      },
      { root: this._track, threshold: [0, 0.2, 0.6, 1] }
    );

    for (const slide of this._slides) {
      this._intersectionObserver.observe(slide);
    }
  }

  _teardownObserver() {
    if (!this._intersectionObserver) return;
    this._intersectionObserver.disconnect();
    this._intersectionObserver = null;
  }

  _activateSlide(slide) {
    const idx = this._slides.indexOf(slide);
    const info = this._mediaInfo[idx];
    if (!info) return;

    if (info.kind === 'uploaded') {
      this._hydrateUploadedVideo(slide);
      this._attachActiveVideo(slide.querySelector('video'));
    } else if (info.kind === 'direct') {
      this._hydrateDirectVideo(slide, info.src);
      this._attachActiveVideo(slide.querySelector('video'));
    } else if (info.kind === 'iframe') {
      this._hydrateIframe(slide, info);
    }
  }

  _deactivateSlide(slide) {
    const idx = this._slides.indexOf(slide);
    const info = this._mediaInfo[idx];
    if (!info) return;

    if (info.kind === 'uploaded' || info.kind === 'direct') {
      const video = slide.querySelector('video');
      if (video) {
        if (!video.paused) video.pause();
        // Rewind so when the user scrolls back to this slide, the video
        // starts from the beginning rather than resuming mid-clip.
        if (video.readyState >= 1) {
          try { video.currentTime = 0; } catch {}
        }
      }
      slide.classList.remove('asoy-slide--paused');
      slide.style.setProperty('--asoy-progress', '0');
      if (this._activeVideo === video) this._detachActiveVideoListeners();
    } else if (info.kind === 'iframe') {
      this._destroyIframe(slide);
    }
  }

  _hydrateUploadedVideo(slide) {
    const video = slide.querySelector('video[data-asoy-video]');
    if (!video) return;
    // Sources are already populated in Liquid (with #t=0.001 fragment so
    // iOS Safari shows the first frame as a free poster). Just bump
    // preload to 'auto' so the active slide is buffered for playback.
    if (!video.dataset.asoyHydrated) {
      video.preload = 'auto';
      video.dataset.asoyHydrated = 'true';
    }
    video.muted = !this._unmuted;
    tryPlay(video);
  }

  _hydrateDirectVideo(slide, src) {
    const mediaWrap = slide.querySelector('[data-asoy-media]');
    if (!mediaWrap) return;

    let video = mediaWrap.querySelector('video[data-asoy-video]');
    if (!video) {
      video = document.createElement('video');
      video.className = 'asoy-slide__video';
      video.dataset.asoyVideo = '';
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      // preload="metadata" + #t=0.001 = iOS Safari renders the first frame
      // as a free poster instead of opaque black. Bumped to 'auto' on
      // activation just below.
      video.preload = 'metadata';
      video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback noplaybackrate');
      video.disablePictureInPicture = true;

      // Poster precedence:
      //   1. desktop-cached extracted frame (best)
      //   2. the same placeholder SVG that the slide's poster div is
      //      already rendering — guarantees iOS shows that SVG instead
      //      of opaque black while #t=0.001 is loading.
      const cachedPoster = posterCache.get(src);
      const slidePosterSvg = slide.querySelector('.asoy-slide__poster svg');
      video.poster = cachedPoster || svgToDataUrl(slidePosterSvg);

      const fragment = src.includes('#') ? '' : '#t=0.001';
      video.src = src + fragment;

      const playpauseBtn = mediaWrap.querySelector('[data-asoy-playpause]');
      mediaWrap.insertBefore(video, playpauseBtn || null);
      // Explicit load() — iOS sometimes won't honour the preload hint
      // for JS-inserted videos without it.
      try { video.load(); } catch {}
      video.dataset.asoyHydrated = 'true';
    }
    video.preload = 'auto';
    video.muted = !this._unmuted;
    tryPlay(video);
  }

  _hydrateIframe(slide, info) {
    const mediaWrap = slide.querySelector('[data-asoy-media]');
    if (!mediaWrap || mediaWrap.querySelector('iframe')) return;

    // The Liquid render put an eager <video src="<youtube/etc URL>"> in here
    // so iOS would show the placeholder as the natural poster. For iframe
    // providers we now swap it out for the real provider iframe.
    const failingVideo = mediaWrap.querySelector('video[data-asoy-video]');
    if (failingVideo) failingVideo.remove();

    const iframe = document.createElement('iframe');
    iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
    if (info.provider === 'generic') {
      // Add sandbox for unknown providers to limit risk
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation allow-popups');
    }
    iframe.src = info.embedUrl;

    const playpauseBtn = mediaWrap.querySelector('[data-asoy-playpause]');
    mediaWrap.insertBefore(iframe, playpauseBtn || null);
  }

  _destroyIframe(slide) {
    const iframe = slide.querySelector('iframe');
    if (iframe) iframe.remove();
  }

  _attachActiveVideo(video) {
    if (!video) return;
    if (this._activeVideo === video) return;

    this._detachActiveVideoListeners();

    const slide = video.closest('.asoy-slide');
    this._activeVideo = video;
    this._activeSlide = slide;

    this._activeTimeUpdate = () => {
      if (!video.duration || !isFinite(video.duration)) return;
      const ratio = video.currentTime / video.duration;
      slide?.style.setProperty('--asoy-progress', ratio.toFixed(4));
    };
    this._activePlay = () => slide?.classList.remove('asoy-slide--paused');
    this._activePause = () => slide?.classList.add('asoy-slide--paused');
    this._activeEnd = () => slide?.style.setProperty('--asoy-progress', '0');

    video.addEventListener('timeupdate', this._activeTimeUpdate);
    video.addEventListener('play', this._activePlay);
    video.addEventListener('pause', this._activePause);
    video.addEventListener('ended', this._activeEnd);
  }

  _detachActiveVideoListeners() {
    if (!this._activeVideo) return;
    this._activeVideo.removeEventListener('timeupdate', this._activeTimeUpdate);
    this._activeVideo.removeEventListener('play', this._activePlay);
    this._activeVideo.removeEventListener('pause', this._activePause);
    this._activeVideo.removeEventListener('ended', this._activeEnd);
    this._activeVideo = null;
    this._activeSlide = null;
    this._activeTimeUpdate = null;
  }

  _togglePlayPause() {
    const video = this._activeVideo;
    if (!video) return;
    if (video.paused) {
      tryPlay(video);
    } else {
      video.pause();
    }
  }

  _toggleMute() {
    this._unmuted = !this._unmuted;
    this._dialog?.classList.toggle('asoy-modal--unmuted', this._unmuted);
    const muteBtn = this._dialog?.querySelector('[data-asoy-mute]');
    if (muteBtn) muteBtn.setAttribute('aria-pressed', String(!this._unmuted));

    // Apply to every video, not just the (possibly-stale) active one.
    // Cheap, and guarantees the click always does something visible.
    for (const slide of this._slides) {
      const video = slide.querySelector('video[data-asoy-video]');
      if (video) video.muted = !this._unmuted;
    }
  }

  _pauseAllMedia() {
    for (const slide of this._slides) {
      const video = slide.querySelector('video');
      if (video) {
        video.pause();
        // Force-mute on close so any pending tryPlay() canplay handler
        // can't resume audible playback after the modal is gone.
        video.muted = true;
        if (video.readyState >= 1) {
          try { video.currentTime = 0; } catch {}
        }
      }
      slide.style.setProperty('--asoy-progress', '0');
      slide.classList.remove('asoy-slide--paused');
      this._destroyIframe(slide);
    }
  }
}

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

const IS_IOS = typeof navigator !== 'undefined'
  && /iPad|iPhone|iPod/.test(navigator.userAgent)
  && !window.MSStream;

// 1×1 transparent PNG fallback if we can't find an SVG to use as poster.
const TRANSPARENT_POSTER = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

// Serialise an inline <svg> to a data: URL so it can drive a <video poster="">.
// iOS Safari needs the poster to fully cover the video element — a 1×1 PNG
// + object-fit:cover stretches it, but a properly-sized SVG looks intentional.
function svgToDataUrl(svgElement) {
  if (!svgElement) return TRANSPARENT_POSTER;
  if (!svgElement.getAttribute('xmlns')) {
    svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }
  const serialised = new XMLSerializer().serializeToString(svgElement);
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(serialised);
}

// iOS Safari frequently rejects the FIRST video.play() — either because the
// gesture context has already elapsed by the time data arrives, or because
// preload="none" left the element with too little buffered to start. Retry
// once on `canplay`; if iOS still says no, the user can tap our play button.
// IMPORTANT: bail in the canplay handler if the modal closed in the
// meantime, otherwise the modal can keep emitting audio after the user
// closed it.
function tryPlay(video) {
  if (!video) return;
  let p;
  try {
    p = video.play();
  } catch {
    return;
  }
  if (!p?.catch) return;
  p.catch(() => {
    const onReady = () => {
      video.removeEventListener('canplay', onReady);
      const dialog = video.closest('dialog');
      // Don't auto-play if the dialog has closed since we registered.
      if (!video.isConnected || (dialog && !dialog.open)) return;
      video.play().catch(() => {});
    };
    video.addEventListener('canplay', onReady, { once: true });
  });
}

// Module-scoped cache so two items pointing at the same URL only extract once.
const posterCache = new Map();
const posterPending = new Map();

function extractPoster(url) {
  if (posterCache.has(url)) return Promise.resolve(posterCache.get(url));
  if (posterPending.has(url)) return posterPending.get(url);

  // iOS Safari refuses to load offscreen/hidden videos AND aggressively
  // taints cross-origin canvases — toDataURL() throws every time. Bail
  // immediately instead of holding a hidden <video> around for 6s.
  if (IS_IOS) {
    posterCache.set(url, null);
    return Promise.resolve(null);
  }

  const pending = new Promise((resolve) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.style.cssText = 'position:absolute;inline-size:1px;block-size:1px;opacity:0;pointer-events:none;';

    let settled = false;
    const cleanup = (result) => {
      if (settled) return;
      settled = true;
      try { video.remove(); } catch {}
      if (result) posterCache.set(url, result);
      posterPending.delete(url);
      resolve(result || null);
    };

    video.addEventListener(
      'loadeddata',
      () => {
        // Seek a touch past frame 0 in case the very first frame is black.
        try {
          video.currentTime = Math.min(0.1, (video.duration || 1) * 0.05);
        } catch {}
      },
      { once: true }
    );
    video.addEventListener(
      'seeked',
      () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 720;
          canvas.height = video.videoHeight || 1280;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          cleanup(canvas.toDataURL('image/jpeg', 0.75));
        } catch {
          // Tainted canvas — CDN didn't send CORS headers. Give up gracefully.
          cleanup(null);
        }
      },
      { once: true }
    );
    video.addEventListener('error', () => cleanup(null), { once: true });

    // Belt and braces — never hang past 6s.
    setTimeout(() => cleanup(null), 6000);

    document.body.appendChild(video);
    video.src = url;
  });

  posterPending.set(url, pending);
  return pending;
}

if (!customElements.get('as-seen-on-you')) {
  customElements.define('as-seen-on-you', AsSeenOnYou);
}
